import { useState, type ReactNode } from "react";

import { Button } from "./button.js";
import { cn } from "./cn.js";

/**
 * Asking before something irreversible, in the place it is about.
 *
 * Not `window.confirm`: that is a browser chrome dialog with the origin in it, no
 * theme, no wording of ours and no room to say what is about to be lost. The question
 * belongs where the thing being deleted is, so it is asked there and answered there.
 */
export function ConfirmStrip({
  busy = false,
  children,
  className,
  confirmLabel,
  onCancel,
  onConfirm,
  question,
}: {
  busy?: boolean;
  children?: ReactNode;
  className?: string | undefined;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
  question: string;
}) {
  return (
    <div
      aria-label={question}
      className={cn(
        "flex flex-wrap items-center gap-3 rounded-sm border border-[color-mix(in_oklch,var(--err)_35%,var(--border))] bg-[color-mix(in_oklch,var(--err)_8%,transparent)] px-3 py-2",
        className,
      )}
      role="alertdialog"
    >
      <div className="min-w-0 flex-1 text-xs leading-5 text-ink">
        <span className="font-semibold">{question}</span>
        {children ? <span className="text-ink-2"> {children}</span> : null}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button disabled={busy} onClick={onCancel} size="small" variant="ghost">
          Cancel
        </Button>
        <Button
          className="border-transparent bg-err text-[oklch(0.99_0_0)] hover:bg-[color-mix(in_oklch,var(--err)_85%,black)]"
          disabled={busy}
          onClick={onConfirm}
          size="small"
        >
          {busy ? "Working…" : confirmLabel}
        </Button>
      </div>
    </div>
  );
}

/**
 * The strip and its trigger together, for the places where the question can simply
 * take the place of the button that asked it.
 */
export function InlineConfirm({
  busy = false,
  children,
  className,
  confirmLabel,
  onConfirm,
  question,
  trigger,
}: {
  busy?: boolean;
  children?: ReactNode;
  className?: string | undefined;
  confirmLabel: string;
  onConfirm: () => void;
  question: string;
  trigger: (open: () => void) => ReactNode;
}) {
  const [asking, setAsking] = useState(false);

  if (!asking) {
    return (
      <>
        {trigger(() => {
          setAsking(true);
        })}
      </>
    );
  }

  return (
    <ConfirmStrip
      busy={busy}
      className={className}
      confirmLabel={confirmLabel}
      onCancel={() => {
        setAsking(false);
      }}
      onConfirm={onConfirm}
      question={question}
    >
      {children}
    </ConfirmStrip>
  );
}
